import { apiFetch } from "../lib/api";
import type { BrokerKind } from "./brokers";
import type { DashboardSummary } from "./dashboard";
import type { Deployment } from "./deployments";

export interface AdminUserRow {
  id: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  can_backtest: boolean;
  can_backfill: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface BrokerPolicyRow {
  broker: BrokerKind;
  enabled: boolean;
  max_accounts: number;
  /** Broker ACCOUNTS configured — not trading activity. It's the one number the
   *  admin surface exposes about another user's resources, because without it
   *  you'd be setting caps blind. */
  used: number;
}

export interface AdminUserDetail extends AdminUserRow {
  broker_policies: BrokerPolicyRow[];
  strategy_keys: string[];
}

export interface BrokerPolicyIn {
  broker: BrokerKind;
  enabled: boolean;
  max_accounts: number;
}

export interface AdminUserCreate {
  email: string;
  password: string;
  display_name?: string | null;
  is_admin?: boolean;
  can_backtest?: boolean;
  can_backfill?: boolean;
  grant_all_strategies?: boolean;
  strategy_keys?: string[];
  broker_policies?: BrokerPolicyIn[];
}

export interface AdminUserUpdate {
  display_name?: string | null;
  is_admin?: boolean;
  is_active?: boolean;
  can_backtest?: boolean;
  can_backfill?: boolean;
}

export interface StrategyGrantsView {
  granted: string[];
  available: string[];
}

export interface AdminStrategyRow {
  key: string;
  display_name: string;
}

export interface AuditLogRow {
  id: string;
  actor_email: string | null;
  event: string;
  target_id: string | null;
  payload_json: string | null;
  created_at: string;
}

export async function listUsers(): Promise<AdminUserRow[]> {
  return apiFetch<AdminUserRow[]>("/admin/users");
}

export async function getUser(id: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${id}`);
}

export async function createUser(payload: AdminUserCreate): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>("/admin/users", { method: "POST", body: payload });
}

export async function updateUser(
  id: string,
  payload: AdminUserUpdate,
): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function resetPassword(id: string, newPassword: string): Promise<void> {
  await apiFetch<void>(`/admin/users/${id}/password`, {
    method: "POST",
    body: { new_password: newPassword },
  });
}

export async function setBrokerPolicies(
  id: string,
  rows: BrokerPolicyIn[],
): Promise<BrokerPolicyRow[]> {
  return apiFetch<BrokerPolicyRow[]>(`/admin/users/${id}/broker-policies`, {
    method: "PUT",
    body: rows,
  });
}

export async function setStrategyGrants(
  id: string,
  strategyKeys: string[],
): Promise<StrategyGrantsView> {
  return apiFetch<StrategyGrantsView>(`/admin/users/${id}/strategy-grants`, {
    method: "PUT",
    body: { strategy_keys: strategyKeys },
  });
}

export async function listAllStrategies(): Promise<AdminStrategyRow[]> {
  return apiFetch<AdminStrategyRow[]>("/admin/strategies");
}

export async function listAudit(limit = 100): Promise<AuditLogRow[]> {
  return apiFetch<AuditLogRow[]>(`/admin/audit?limit=${limit}`);
}

// ---------------------------------------------------------------------------
// Oversight — another user's P&L and deployments. Stop/Kill go through the
// ordinary /deployments/{id}/stop|kill routes, which admit an admin.
// ---------------------------------------------------------------------------

export type OversightMode = "paper" | "live";

export interface PnlDailyRow {
  /** IST calendar date, YYYY-MM-DD. */
  day: string;
  realized_pnl: string;
  fills: number;
}

export interface AdminLiveRow {
  deployment_id: string;
  name: string;
  strategy_key: string;
  mode: "paper" | "live";
  status: string;
  owner_id: string;
  owner_email: string;
  owner_name: string | null;
  day_realized_pnl: string;
  unrealized_pnl: string;
  open_positions: number;
  seconds_since_last_tick: number | null;
  feed_alert: string | null;
}

function modeQs(mode?: OversightMode): string {
  return mode ? `?mode=${mode}` : "";
}

export async function getUserPnlSummary(
  id: string,
  mode?: OversightMode,
): Promise<DashboardSummary> {
  return apiFetch<DashboardSummary>(`/admin/users/${id}/summary${modeQs(mode)}`);
}

export async function getUserPnlDaily(
  id: string,
  mode?: OversightMode,
): Promise<PnlDailyRow[]> {
  return apiFetch<PnlDailyRow[]>(`/admin/users/${id}/pnl-daily${modeQs(mode)}`);
}

export async function getUserDeployments(
  id: string,
  mode?: OversightMode,
): Promise<Deployment[]> {
  return apiFetch<Deployment[]>(`/admin/users/${id}/deployments${modeQs(mode)}`);
}

export async function getLiveNow(mode?: OversightMode): Promise<AdminLiveRow[]> {
  return apiFetch<AdminLiveRow[]>(`/admin/live${modeQs(mode)}`);
}

export interface BrokerChannelStatus {
  channel: string;
  ok: boolean;
  message: string;
  latency_ms: number | null;
}

/** One of a user's broker connections and its last known health. Never
 *  carries credentials. `ok`/`checked_at` are null until it's been checked
 *  (the cache lives in the backend's memory). */
export interface AdminBrokerRow {
  id: string;
  broker: string;
  label: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  checked_at: string | null;
  ok: boolean | null;
  channels: BrokerChannelStatus[];
}

export async function getUserBrokers(id: string): Promise<AdminBrokerRow[]> {
  return apiFetch<AdminBrokerRow[]>(`/admin/users/${id}/brokers`);
}

/** Runs the connectivity check now, with the user's own credentials. */
export async function checkUserBrokers(id: string): Promise<AdminBrokerRow[]> {
  return apiFetch<AdminBrokerRow[]>(`/admin/users/${id}/brokers/check`, {
    method: "POST",
  });
}
